import { palette } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { DISPATCH_STATUSES, STATUS_FLOW, PRIORITY_STYLES, nextStatus } from './config';
import type { DispatchJob } from './hooks';

interface Props {
  job: DispatchJob | null;
  onClose: () => void;
  onAdvance: (jobId: string, status: string) => void;
  onReassign: (job: DispatchJob) => void;
  onCancel: (jobId: string) => void;
}

function fmtTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' });
}

// TODO: Mapbox GL JS integration — show job location pin on embedded map

export function DispatchDetailModal({ job, onClose, onAdvance, onReassign, onCancel }: Props) {
  if (!job) return null;

  const st = DISPATCH_STATUSES[job.status] ?? DISPATCH_STATUSES.new;
  const pr = PRIORITY_STYLES[job.priority ?? 'medium'] ?? PRIORITY_STYLES.medium;
  const next = nextStatus(job.status);
  const nextLabel = next ? DISPATCH_STATUSES[next]?.label : null;
  const isActive = ['assigned', 'en_route', 'on_site'].includes(job.status);
  const raw = job as unknown as Record<string, unknown>;

  return (
    <SlidePanel isOpen={!!job} onClose={onClose} title={job.title} subtitle={`#${job.id.slice(0, 8)}`} width={520}>
      {/* Status + Priority */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: st.bg, color: st.color }}>{st.label}</span>
        <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: pr.bg, color: pr.fg }}>{pr.label} priority</span>
      </div>

      {/* Status pipeline */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {STATUS_FLOW.map(s => {
          const filled = STATUS_FLOW.indexOf(job.status) >= STATUS_FLOW.indexOf(s);
          return <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: filled ? palette.accent : palette.g200 }} />;
        })}
      </div>

      {/* Details */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16, fontSize: 13 }}>
        <div><span style={{ color: palette.g400 }}>Client</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{job.client_name ?? '—'}</div></div>
        <div><span style={{ color: palette.g400 }}>Phone</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{(job as unknown as Record<string, unknown>).client_phone as string ?? '—'}</div></div>
        <div><span style={{ color: palette.g400 }}>Address</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{job.address ?? job.location ?? '—'}</div></div>
        <div><span style={{ color: palette.g400 }}>Scheduled</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{job.scheduled_date ?? '—'} {job.scheduled_time ?? ''}</div></div>
      </div>

      {/* Crew */}
      {job.assigned_name && (
        <div style={{ padding: '10px 12px', background: palette.g50, borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
          <span style={{ color: palette.g400 }}>Assigned to:</span>
          <span style={{ fontWeight: 600, color: palette.g800, marginLeft: 6 }}>{job.assigned_name}</span>
          {job.assigned_phone && <span style={{ color: palette.g400, marginLeft: 8 }}>{job.assigned_phone}</span>}
        </div>
      )}

      {/* Timeline */}
      <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: palette.g600, marginBottom: 8 }}>Timeline</div>
        {[
          { label: 'Created', time: job.created_at },
          { label: 'Dispatched', time: raw.dispatched_at as string ?? (job.status !== 'new' ? job.updated_at : null) },
          { label: 'En Route', time: raw.en_route_at as string },
          { label: 'On Site', time: raw.started_at as string },
          { label: 'Completed', time: job.completed_at },
        ].map(entry => (
          <div key={entry.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
            <span style={{ width: 70, color: palette.g400 }}>{entry.label}</span>
            <span style={{ fontWeight: 500, color: entry.time ? palette.g700 : palette.g300 }}>{fmtTime(entry.time)}</span>
          </div>
        ))}
      </div>

      {/* Description */}
      {job.description && (
        <div style={{ padding: '10px 12px', background: palette.g50, borderRadius: 6, marginBottom: 16, fontSize: 13, color: palette.g600 }}>
          {job.description}
        </div>
      )}

      {/* Actions */}
      {isActive && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {next && nextLabel && (
            <button onClick={() => onAdvance(job.id, next)} style={{
              padding: '7px 16px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 600,
              background: palette.accent, color: palette.white, cursor: 'pointer', fontFamily: 'inherit',
            }}>Mark {nextLabel}</button>
          )}
          <button onClick={() => onReassign(job)} style={{
            padding: '7px 16px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 12, fontWeight: 500,
            background: palette.white, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit',
          }}>Reassign</button>
          <button onClick={() => onCancel(job.id)} style={{
            padding: '7px 16px', borderRadius: 6, border: `1px solid ${palette.red}`, fontSize: 12, fontWeight: 500,
            background: palette.redWash, color: palette.red, cursor: 'pointer', fontFamily: 'inherit',
          }}>Cancel dispatch</button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
      </div>
    </SlidePanel>
  );
}
